/**
 * Profile customization catalogue. The ids are stored on the user and validated
 * by the server; the web app renders them. Everything here before Blaze is free
 * for everyone, for good; the extras marked `blaze` need ClanFire Blaze, and
 * stop showing (but stay chosen) while someone doesn't have it. Seasonal ones
 * (`season`) are free for good too; the picker only marks them in their season.
 */
import { APP_NAME } from './brand';

export const AVATAR_DECORATIONS = [
  { id: 'flame', label: `${APP_NAME} Flame` },
  { id: 'neon', label: 'Neon Glow' },
  { id: 'rainbow', label: 'Rainbow Ring' },
  { id: 'frost', label: 'Frostbite' },
  { id: 'sakura', label: 'Sakura' },
  { id: 'leaves', label: 'Leafy Wreath' },
  { id: 'crown', label: 'Royal Crown' },
  { id: 'halo', label: 'Halo' },
  { id: 'cat-ears', label: 'Cat Ears' },
  { id: 'hearts', label: 'Orbiting Hearts' },
  { id: 'stars', label: 'Starlight' },
  { id: 'glitch', label: 'Glitch' },
  { id: 'pumpkin', label: "Jack-o'-lantern", season: 'halloween' },
  { id: 'phoenix', label: 'Phoenix', blaze: true },
  { id: 'inferno', label: 'Inferno', blaze: true },
] as const;
export type AvatarDecorationId = (typeof AVATAR_DECORATIONS)[number]['id'];

export const PROFILE_EFFECTS = [
  { id: 'embers', label: 'Embers' },
  { id: 'snow', label: 'Snowfall' },
  { id: 'sparkles', label: 'Sparkles' },
  { id: 'confetti', label: 'Confetti' },
  { id: 'hearts', label: 'Floating Hearts' },
  { id: 'bubbles', label: 'Bubbles' },
  { id: 'aurora', label: 'Aurora' },
  { id: 'rain', label: 'Rainy Day' },
  { id: 'bats', label: 'Bats', season: 'halloween' },
  { id: 'wildfire', label: 'Wildfire', blaze: true },
  { id: 'fireflies', label: 'Fireflies', blaze: true },
] as const;
export type ProfileEffectId = (typeof PROFILE_EFFECTS)[number]['id'];

export const NAME_FONTS = [
  { id: 'rounded', label: 'Rounded' },
  { id: 'serif', label: 'Serif' },
  { id: 'mono', label: 'Mono' },
  { id: 'script', label: 'Script' },
  { id: 'condensed', label: 'Bold' },
  { id: 'wide', label: 'Wide' },
] as const;
export type NameFontId = (typeof NAME_FONTS)[number]['id'];

export const NAME_EFFECTS = [
  { id: 'solid', label: 'Solid' },
  { id: 'gradient', label: 'Gradient' },
  { id: 'neon', label: 'Neon' },
  { id: 'rainbow', label: 'Rainbow' },
  { id: 'ghostly', label: 'Ghostly', season: 'halloween' },
  { id: 'blaze', label: 'Blaze', blaze: true },
  { id: 'shimmer', label: 'Shimmer', blaze: true },
] as const;
export type NameEffectId = (typeof NAME_EFFECTS)[number]['id'];

export const idsOf = <T extends ReadonlyArray<{ id: string }>>(list: T) => list.map((o) => o.id) as [T[number]['id'], ...Array<T[number]['id']>];

/** Whether this is a Halloween style, which the picker marks during October. */
export function isHalloweenStyle(list: ReadonlyArray<{ id: string; season?: string }>, id: string): boolean {
  return list.some((o) => o.id === id && o.season === 'halloween');
}

/** Whether this style needs ClanFire Blaze. */
export function isBlazeStyle(list: ReadonlyArray<{ id: string; blaze?: boolean }>, id: string | null | undefined): boolean {
  return !!id && list.some((o) => o.id === id && o.blaze === true);
}
